import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import { Painel } from '../../../../components/Painel';
import { Icon } from '../../../../components/Icon';
import { StatCard } from '../../../../components/StatCard';
import { colors } from '../../../../styles/tokens';
import { useToast } from '../../../../hooks/toast';
import { GymsService, IGymDashboard } from '../../../../services/GymsService';

import { AttendanceChart } from '../../../dashboard/pages/dashboard/components/AttendanceChart';
import { RecentActivity } from '../../../dashboard/pages/dashboard/components/RecentActivity';

import {
  BackButton,
  Content,
  Header,
  HeaderActions,
  HeaderInfo,
  HeaderTop,
  MainGrid,
  PageSubtitle,
  PageTitle,
  StatsRow,
  StatusBadge,
  UnlockButton,
} from './styles';

const GymDetails: React.FC = () => {
  const { gym_id } = useParams<{ gym_id: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();

  const gymsService = new GymsService();

  const [data, setData] = useState<IGymDashboard>();
  const [loading, setLoading] = useState(true);
  const [unlocking, setUnlocking] = useState(false);

  const getGymDashboard = useCallback(() => {
    if (!gym_id) return;

    gymsService
      .getGymDashboard(gym_id)
      .then(response => {
        setData(response.data);
      })
      .catch(() => {
        addToast({
          type: 'error',
          title: 'Não foi possível carregar a academia',
        });
        navigate('/admin/gyms');
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gym_id]);

  useEffect(() => {
    getGymDashboard();
  }, [getGymDashboard]);

  const handleUnlock = useCallback(() => {
    if (!gym_id) return;

    setUnlocking(true);

    gymsService
      .unlock(gym_id)
      .then(() => {
        addToast({
          type: 'success',
          title: 'Academia desbloqueada',
        });
        getGymDashboard();
      })
      .catch(() => {
        addToast({
          type: 'error',
          title: 'Erro ao desbloquear academia',
        });
      })
      .finally(() => {
        setUnlocking(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gym_id, getGymDashboard]);

  return (
    <Painel loading={loading}>
      {!!data && (
        <Content>
          <Header>
            <BackButton type="button" onClick={() => navigate('/admin/gyms')}>
              <Icon
                name="arrow_back"
                size={18}
                color={colors.onSurfaceVariant}
              />
              Academias
            </BackButton>

            <HeaderTop>
              <HeaderInfo>
                <StatusBadge is_active={data.gym.is_active}>
                  {data.gym.is_active ? 'Ativa' : 'Bloqueada'}
                </StatusBadge>

                <PageTitle>{data.gym.name}</PageTitle>
                <PageSubtitle>
                  {data.gym.domain} {'• '}
                  Plano {data.gym.signature?.toUpperCase()} {'• '}
                  criada em{' '}
                  {format(new Date(data.gym.created_at), 'dd/MM/yyyy', {
                    locale: ptBR,
                  })}
                </PageSubtitle>
              </HeaderInfo>

              <HeaderActions>
                <UnlockButton
                  type="button"
                  onClick={() => navigate(`/admin/gyms/${gym_id}/features`)}
                >
                  <Icon name="storefront" size={18} color={colors.onPrimary} />
                  Features & addons
                </UnlockButton>

                {!data.gym.is_active && (
                  <UnlockButton
                    type="button"
                    disabled={unlocking}
                    onClick={handleUnlock}
                  >
                    <Icon
                      name="lock_open"
                      size={18}
                      color={colors.onPrimary}
                    />
                    Desbloquear academia
                  </UnlockButton>
                )}
              </HeaderActions>
            </HeaderTop>
          </Header>

          <StatsRow>
            <StatCard title="Alunos cadastrados" data={data.usersRegistered} />
            <StatCard
              title="Exercícios cadastrados"
              data={data.exercisesRegistered}
            />
            <StatCard
              title="Alunos ativos na semana"
              data={data.usersActiveInCurrentWeek}
            />
            <StatCard
              title="Novos alunos no mês"
              data={data.usersRegisteredThisMonth}
            />
          </StatsRow>

          <MainGrid>
            <AttendanceChart
              data={data.weeklyFrequency}
              totalUsers={data.usersRegistered}
            />
            <RecentActivity activities={data.recentActivity} />
          </MainGrid>
        </Content>
      )}
    </Painel>
  );
};

export { GymDetails };
